import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Package, Clock, Truck, Loader2, XCircle } from 'lucide-react';
import './CustomerOrderDetails.css';
import { ORDER_STATUS } from "../utils/orderStatus";
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CustomerOrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const fetchOrderDetails = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/orders/${id}`);
            const data = await res.json();
            if (data.success) {
                setOrder(data.data);
            } else {
                setError(data.message || 'Failed to fetch order details');
            }
        } catch (err) {
            console.error('Error fetching order details:', err);
            setError('An error occurred connecting to the server.');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    if (loading) {
        return (
            <div className="cod-page-container fade-in">
                <div className="cod-top-nav">
                    <button onClick={() => navigate(-1)} className="back-link">
                        <ArrowLeft size={20} />
                        <span>Back to Orders</span>
                    </button>
                </div>
                <div className="cod-loading-state">
                    <Loader2 className="spin-icon" size={40} color="#db2777" />
                </div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="cod-page-container fade-in">
                <div className="cod-top-nav">
                    <button onClick={() => navigate(-1)} className="back-link">
                        <ArrowLeft size={20} />
                        <span>Back to Orders</span>
                    </button>
                </div>
                <div className="cod-error-state">
                    <p>{error || 'Order not found'}</p>
                    <button className="btn-return" onClick={() => navigate(-1)}>Return to Orders</button>
                </div>
            </div>
        );
    }

    const isCancelled = order.status === 'Cancelled';


    const statuses = [
        { key: 'placed', label: ORDER_STATUS.placed.label, index: 1, icon: <Package size={20} /> },
        { key: 'accepted', label: ORDER_STATUS.accepted.label, index: 2, icon: <Check size={20} /> },
        { key: 'inprogress', label: ORDER_STATUS.inprogress.label, index: 3, icon: <Clock size={20} /> },
        { key: 'shipped', label: ORDER_STATUS.shipped.label, index: 4, icon: <Truck size={20} /> }
    ];


    // Determine current progress
    const statusMatch = statuses.find(s => s.key === order.status);
    const currentIndex = statusMatch ? statusMatch.index : 0;

    return (
        <div className="cod-page-container fade-in">
            <div className="cod-top-nav">
                <button onClick={() => navigate(-1)} className="back-link">
                    <ArrowLeft size={20} />
                    <span>Back to Orders</span>
                </button>
            </div>

            <div className="cod-content">
                <div className="cod-main-header">
                    <div className="cod-header-text">
                        <h1>Order Details</h1>
                        <p className="cod-id">Order ID: #{order.id.split('-')[0]}</p>
                        <p className="cod-date">Placed on {formatDateTime(order.created_at)}</p>
                    </div>
                    <div className="cod-header-price">
                        <p className="cod-total-label">Total Amount</p>
                        <p className="cod-total-amount">₹{Number(order.total_amount).toLocaleString('en-IN')}</p>
                    </div>
                </div>

                <div className={`cod-section ${isCancelled ? 'cod-cancelled-section' : 'cod-status-section'}`}>
                    <h2 className="cod-section-title">{isCancelled ? 'Order Status' : 'Order Status'}</h2>
                    {isCancelled ? (
                        <div className="cod-cancelled-container">
                            <div className="cod-cancelled-icon-wrapper">
                                <XCircle size={64} className="cod-cancelled-icon" />
                            </div>
                            <h3 className="cod-cancelled-title">Order Cancelled</h3>
                            <p className="cod-cancelled-message">This order has been cancelled by the seller.</p>
                        </div>
                    ) : (
                        <div className="cod-timeline-container">
                            <div className="cod-timeline-track">
                                <div
                                    className="cod-timeline-fill"
                                    style={{ width: `${((currentIndex - 1) / (statuses.length - 1)) * 100}%` }}
                                ></div>
                            </div>

                            <div className="cod-timeline-steps">
                                {statuses.map((step) => {
                                    const isCompleted = currentIndex >= step.index;
                                    const isCurrent = currentIndex === step.index;

                                    return (
                                        <div key={step.key} className={`cod-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                                            <div className="cod-step-circle">
                                                {step.icon}
                                            </div>
                                            <div className="cod-step-label">{step.label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="cod-section">
                    <h2 className="cod-section-title">Order Items</h2>
                    <div className="cod-items-list">
                        {order.items && order.items.map((item, idx) => (
                            <div key={idx} className="cod-item-row">
                                <div className="cod-item-info">
                                    <img
                                        src={item.product_image || '/uploads/placeholder.jpg'}
                                        alt={item.product_name}
                                        className="cod-item-image"
                                        onError={(e) => { e.target.src = '/uploads/placeholder.jpg'; }}
                                    />
                                    <div className="cod-item-details">
                                        <h4>{item.product_name}</h4>
                                        <p className="cod-item-category">Sarees</p>
                                        <p className="cod-item-qty">Quantity: {item.quantity}</p>
                                    </div>
                                </div>
                                <div className="cod-item-price">
                                    ₹{Number(item.price * item.quantity).toLocaleString('en-IN')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="cod-section">
                    <h2 className="cod-section-title">Delivery Information</h2>
                    <div className="cod-delivery-grid">
                        <div className="cod-grid-item">
                            <label>Customer Name</label>
                            <p>{order.delivery_name}</p>
                        </div>
                        <div className="cod-grid-item">
                            <label>Phone Number</label>
                            <p>{order.delivery_phone}</p>
                        </div>
                        <div className="cod-grid-item">
                            <label>Email</label>
                            <p>{order.delivery_email || order.user_email}</p>
                        </div>
                        <div className="cod-grid-item">
                            <label>Pincode</label>
                            <p>{order.delivery_pincode}</p>
                        </div>
                        <div className="cod-grid-item full-width">
                            <label>Delivery Address</label>
                            <p>{order.delivery_address}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerOrderDetails;
